import { Box, CircularProgress } from '@mui/material'
import { default as CMSHeader } from 'components/CMSHeader/CMSHeader'
import DrawerGrid from 'components/DrawerGrid'
import { DrawerGridProps, DrawerWidth } from 'components/DrawerGrid/DrawerGrid'
import PagePreview from 'components/PagePreview/PagePreview'
import BlockCreatePanel from 'drawerPanels/BlockCreatePanel/BlockCreatePanel'
import BlockDetailsPanel from 'drawerPanels/BlockDetailsPanel/BlockDetailsPanel'
import BlockSettingsEditPanel from 'drawerPanels/BlockSettingsEditPanel/BlockSettingsEditPanel'
import BlockTreePanel from 'drawerPanels/BlockTreePanel/BlockTreePanel'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStoreActions, useStoreState } from 'store/hooks'
import { NavigationRoutes } from 'utils/navigationRoutes'
import { getParsedPageElements } from 'utils/parseElementContent'
import { EditingDataType } from 'utils/types'

export enum BlockListViewMode {
    PUBLISH = 'PUBLISH',
    SETTINGS = 'SETTINGS',
    BLOCKLIST = 'BLOCKLIST',
    ADD = 'ADD',
    EDIT = 'EDIT',
}

const BlockListPage = (): JSX.Element => {
    //data
    const drawerGridProps: DrawerGridProps = {}

    //hooks and data from store
    const navigate = useNavigate()

    const selectedWebsite = useStoreState((state) => state.model.selectedWebsite)
    const currentlySelectedBlock = useStoreState((state) => state.model.currentlySelectedBlock)
    const unpublishedItems = useStoreState((state) => state.model.unpublishedItems)
    const blockList = useStoreState((state) => state.model.blockList)
    const blockData = useStoreState((state) => state.model.blockData)
    const loggedInUser = useStoreState((state) => state.model.loggedInUser)
    const assetsSimplified = useStoreState((state) => state.model.assetsSimplified)
    const savingCurrentVersionDisabled = useStoreState((state) => state.model.savingCurrentVersionDisabled)
    const websiteList = useStoreState((state) => state.model.websiteList)

    const startEditingPage = useStoreActions((actions) => actions.model.startEditingPage)
    const loadBlock = useStoreActions((actions) => actions.model.loadBlock)
    const deleteBlock = useStoreActions((actions) => actions.model.deleteBlock)
    const duplicateBlock = useStoreActions((actions) => actions.model.duplicateBlock)
    const createBlock = useStoreActions((actions) => actions.model.createBlock)
    const saveBlock = useStoreActions((actions) => actions.model.saveBlock)
    const setSelectedWebsite = useStoreActions((actions) => actions.model.setSelectedWebsite)

    //internal state
    const [viewMode, setViewMode] = useState<BlockListViewMode>(BlockListViewMode.BLOCKLIST)

    //functions
    const selectBlock = (id: string) => {
        if (currentlySelectedBlock === undefined || (currentlySelectedBlock && currentlySelectedBlock.id !== id)) {
            loadBlock(id)
        }
    }

    const onDeleteBlock = (id: string) => {
        if (confirm('Are you sure you want to delete this block?')) {
            deleteBlock(id)
        }
    }

    const onDuplicateBlock = (id: string) => {
        duplicateBlock(id)
    }

    const onSaveBlock = (label: string, identifier: string) => {
        if (currentlySelectedBlock)
            saveBlock({
                blockID: currentlySelectedBlock.id,
                name: label,
                content: currentlySelectedBlock.currentVersion.contents,
                identifier,
            })
        setViewMode(BlockListViewMode.BLOCKLIST)
    }

    const onEditBlock = () => {
        if (currentlySelectedBlock) {
            startEditingPage({
                currentlyEditingData: {
                    id: currentlySelectedBlock.id,
                    identifier: currentlySelectedBlock.identifier,
                    label: currentlySelectedBlock.currentVersion.name,
                    contents: currentlySelectedBlock.currentVersion.contents,
                    type: EditingDataType.BLOCK,
                },
                pageElementContents: getParsedPageElements(currentlySelectedBlock),
            })
            navigate(NavigationRoutes.PAGE_EDIT)
        }
    }

    // ----------------------------------------------------------------------- Loading until webiste and cms data are loaded
    if (!selectedWebsite || !loggedInUser) {
        return (
            <Box sx={{ display: 'flex' }}>
                <CircularProgress />
            </Box>
        )
    }

    drawerGridProps.primary = (
        <BlockTreePanel
            blocks={blockList}
            onSelectBlock={selectBlock}
            onBlockCreate={() => setViewMode(BlockListViewMode.ADD)}
            selectedBlock={currentlySelectedBlock ? currentlySelectedBlock.id : ''}
        />
    )

    if (viewMode === BlockListViewMode.BLOCKLIST) {
        // ------------------------------------------------------------------------- page tree
        if (currentlySelectedBlock) {
            drawerGridProps.secondary = (
                <BlockDetailsPanel
                    block={currentlySelectedBlock}
                    onEditBlock={onEditBlock}
                    onEditSettings={() => setViewMode(BlockListViewMode.EDIT)}
                    onDuplicate={onDuplicateBlock}
                    onDelete={onDeleteBlock}
                    showBlockVersion={() => console.log('showPageVersion')}
                    reactivateBlockVersion={() => console.log('reactivatePageVersion')}
                />
            )
        }
        // primaryDrawerWidth={DrawerWidth.SMALL}
        // secondaryDrawerWidth={DrawerWidth.MEDIUM}
        drawerGridProps.collapsible = true
    }
    // ------------------------------------------------------------------------- edit view
    else if (viewMode === BlockListViewMode.EDIT) {
        drawerGridProps.secondary = (
            <BlockSettingsEditPanel
                saveIsDisabled={savingCurrentVersionDisabled}
                label={currentlySelectedBlock?.currentVersion.name ?? ''}
                onCancel={() => setViewMode(BlockListViewMode.BLOCKLIST)}
                identifier={currentlySelectedBlock?.identifier ?? ''}
                onSubmit={onSaveBlock}
            />
        )

        drawerGridProps.secondaryWidth = DrawerWidth.MEDIUM
        drawerGridProps.collapsible = false
        drawerGridProps.primaryDisabled = true
    }

    // ------------------------------------------------------------------------- add view
    else if (viewMode === BlockListViewMode.ADD) {
        drawerGridProps.secondary = (
            <BlockCreatePanel
                onCancel={() => setViewMode(BlockListViewMode.BLOCKLIST)}
                onSubmit={(label: string) => {
                    createBlock({
                        name: label,
                        websiteID: selectedWebsite.id,
                    })
                    setViewMode(BlockListViewMode.BLOCKLIST) //TODO find a better way of closing the panel after creating
                }}
            />
        )

        drawerGridProps.secondaryWidth = DrawerWidth.MEDIUM
        drawerGridProps.collapsible = false
        drawerGridProps.primaryDisabled = true
    }

    return (
        <>
            <CMSHeader
                unpublishedItems={unpublishedItems}
                selectedWebsite={selectedWebsite?.id ?? ''}
                websiteList={websiteList}
                onSelectWebsite={setSelectedWebsite}
            />
            <DrawerGrid
                {...drawerGridProps}
                content={
                    <PagePreview
                        assets={assetsSimplified}
                        disabled={true}
                        blocks={blockData}
                        pageElements={getParsedPageElements(currentlySelectedBlock)}
                        activeBreakpoint={{
                            identifier: 'xl',
                            name: 'xl',
                            editorCssWidth: '100%',
                            fromWidthPixels: 1536,
                        }}
                    />
                }
            />
        </>
    )
}

export default BlockListPage
